import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import logo from '../../logo_CVUT.jpg';

type ChangelogEntry = { date: string; items: string[] };

const MAIL = 'mailto:';

/**
 * Public "About" page (single, informal page). Mounted outside AuthGate so it
 * mirrors the privacy notice and stays reachable without a session. Changelog
 * entries are loaded dynamically from the `about` i18n namespace so they can be
 * maintained by editing the locale files alone.
 */
export default function AboutPage(): React.ReactElement {
  const { t, i18n } = useTranslation('about');
  const email = t('email');
  const changelog = (t('changelog.entries', { returnObjects: true }) as ChangelogEntry[]) ?? [];

  return (
    <div key={i18n.language} className="min-h-screen bg-ctu-bg-page">
      <header className="border-b border-ctu-border bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-3">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={logo} alt="CTU" className="h-9 w-9 shrink-0 rounded-md object-contain" />
            <span className="text-base font-semibold tracking-tight">Uniconnect</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm text-ctu-text-muted transition-colors hover:text-ctu-text"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('backToApp')}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-ctu-text">{t('pageTitle')}</h1>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('operator.title')}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm leading-relaxed text-ctu-text-muted">
              {t('operator.body')}{' '}
              {/* <a href={`${MAIL}${email}`} className="text-primary hover:underline">
                {email}
              </a> */}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('feedback.title')}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm leading-relaxed text-ctu-text-muted">
              {t('feedback.body')}{' '}
              <a href={`${MAIL}${email}`} className="text-primary hover:underline">
                {email}
              </a>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('funding.title')}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm leading-relaxed text-ctu-text-muted">
              {t('funding.body')}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('changelog.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {changelog.map((entry, i) => (
                <div key={i}>
                  <p className="text-sm font-medium text-ctu-text">{entry.date}</p>
                  <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm leading-relaxed text-ctu-text-muted">
                    {entry.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
